import React, { FC, Fragment } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { AsH1, TitleLG } from '@/atoms/Text'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import locales from '@/constants/locales'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { LandscapeRowCollection, LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import {
  ContinueWatchingProject,
  isContinueWatchingEpisode,
  isContinueWatchingProject,
} from '@/organisms/WatchNowCollection/ContinueWatchingCollection/data'
import { useGuildMember } from '@/services/GuildUserService'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { buildLinkToEpisode, buildProjectEpisodeTitle } from '@/utils/EpisodeUtil'
import { Locale, useLocale } from '@/utils/LocaleUtil'
import { AnalyticsEvent } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

export interface ContinueWatchingCollectionProps {
  toContinueWatching: ContinueWatchingProject[]
}

export const ContinueWatchingCollection: FC<ContinueWatchingCollectionProps> = ({ toContinueWatching }) => {
  const { locale } = useLocale()
  const { t } = useTranslate('watch')
  const { isGuildMember } = useGuildMember()

  return (
    <div data-testid="continue-watching-collection">
      <header>
        <TitleLG weight="bold" as={AsH1} data-testid="continue-watching-header" className="mb-3 md:mb-4">
          {t('continueWatching', 'Continue Watching')}
        </TitleLG>
      </header>
      {/* We get Continue Watching client side so handle hydration here */}
      <LazyHydrate whenVisible>
        <LandscapeRowCollection>
          {toContinueWatching.map((toContinue) => {
            if (!isContinueWatchingProject(toContinue) || !isContinueWatchingEpisode(toContinue.continueWatching)) {
              return null
            }

            const progress =
              ((toContinue.continueWatching?.watchPosition?.position ?? 0) /
                (toContinue.continueWatching.duration ?? 0)) *
              100

            const linkModel: LinkViewModel | null = buildLinkModel(toContinue, locale, progress)
            if (!linkModel) return null

            const title = buildProjectEpisodeTitle({
              projectType: toContinue.projectType as string,
              projectName: toContinue.name as string,
              seasonNumber: toContinue.continueWatching?.season?.seasonNumber || 0,
              episodeNumber: toContinue.continueWatching.episodeNumber || 0,
            })

            return (
              <Fragment key={toContinue.continueWatching.guid}>
                <LandscapeTileCard className="mb-2 mr-4" linkModel={linkModel} key={toContinue.continueWatching.guid}>
                  <div className="absolute inset-0">
                    <div className="flex h-full w-full items-center justify-center opacity-0 duration-200 hover:bg-[#00000088] hover:opacity-100">
                      <div className="mx-auto h-fit w-fit !opacity-100">
                        <PlayCircleIcon />
                      </div>
                    </div>
                  </div>
                  <ProgressOverlay progress={progress} />
                  {toContinue?.continueWatching?.earlyAccessDate && (
                    <GuildAccessNotch
                      earlyAccessDate={new Date(toContinue?.continueWatching?.earlyAccessDate)}
                      publiclyAvailableDate={
                        toContinue?.continueWatching?.publiclyAvailableDate
                          ? new Date(toContinue?.continueWatching?.publiclyAvailableDate)
                          : undefined
                      }
                      isGuildMember={isGuildMember}
                    />
                  )}
                </LandscapeTileCard>
                <TitleAndSubtitleDisplay
                  className="mr-4"
                  title={title}
                  subtitle={toContinue.continueWatching.subtitle}
                />
              </Fragment>
            )
          })}
        </LandscapeRowCollection>
      </LazyHydrate>
    </div>
  )
}

function buildLinkModel(toContinue: ContinueWatchingProject, locale: Locale, progress: number): LinkViewModel | null {
  if (!toContinue.continueWatching) return null

  return {
    linkUrl: buildLinkToEpisode(
      { projectSlug: toContinue.slug as string, guid: toContinue.continueWatching.guid as string },
      { disableDecoration: locale !== locales.defaultLocale },
    ),
    imageUrl: getCloudinaryImageUrl({ path: toContinue.continueWatching.posterLandscapeCloudinaryPath }),
    logoUrl: toContinue.logoCloudinaryPath,
    label: toContinue.name ?? undefined,
    name: toContinue.name ?? undefined,
    track: {
      eventName: 'Clicked Continue Watching' as AnalyticsEvent,
      payload: {
        program: toContinue.continueWatching.guid,
        asset_id: toContinue.continueWatching.guid,
        title: toContinue.continueWatching.name,
        subtitle: toContinue.continueWatching.subtitle,
        progress,
      },
    },
  }
}
